import { useTranslation } from '@jq4race/i18n';
import type { ZoneResponse } from '@jq4race/shared';
import {
  Badge,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@jq4race/ui';
import { Link } from '@tanstack/react-router';
import { Layers, Pencil, Power, PowerOff } from 'lucide-react';

import { STATUS_TONE } from '../constants';

const rowActionClass =
  'inline-flex h-8 w-8 items-center justify-center rounded text-jq-graphite transition hover:bg-jq-surface-2 hover:text-jq-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60';

interface ZonesTableProps {
  zones: ZoneResponse[];
  locationId: string;
  loading: boolean;
  onDeactivate: (zone: ZoneResponse) => void;
  onActivate: (zone: ZoneResponse) => void;
}

export function ZonesTable({
  zones,
  locationId,
  loading,
  onDeactivate,
  onActivate,
}: ZonesTableProps) {
  const { t } = useTranslation('zones');

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('table.headers.name')}</TableHead>
          <TableHead>{t('table.headers.capacity')}</TableHead>
          <TableHead>{t('table.headers.status')}</TableHead>
          <TableHead className="w-[100px] text-right">{t('table.headers.actions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && zones.length === 0 ? (
          <SkeletonRows />
        ) : zones.length === 0 ? (
          <TableEmpty colSpan={4}>
            <EmptyState />
          </TableEmpty>
        ) : (
          zones.map((zone) => (
            <TableRow key={zone.id}>
              <TableCell>
                <span className="font-medium text-jq-charcoal">{zone.name}</span>
              </TableCell>
              <TableCell className="text-jq-graphite">
                {zone.capacity !== null ? zone.capacity : t('table.capacityUnlimited')}
              </TableCell>
              <TableCell>
                <Badge variant={STATUS_TONE[zone.status]} className="capitalize">
                  {t(`status.${zone.status}`)}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <Link
                    to="/dashboard/locations/$locationId/zones/$zoneId/edit"
                    params={{ locationId, zoneId: zone.id }}
                    className={rowActionClass}
                    aria-label={t('table.rowActions.edit', { name: zone.name })}
                  >
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </Link>
                  {zone.status === 'active' ? (
                    <button
                      type="button"
                      onClick={() => onDeactivate(zone)}
                      className={rowActionClass}
                      aria-label={t('table.rowActions.deactivate', { name: zone.name })}
                    >
                      <PowerOff className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onActivate(zone)}
                      className={rowActionClass}
                      aria-label={t('table.rowActions.activate', { name: zone.name })}
                    >
                      <Power className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
        <TableRow key={`skeleton-${i}`}>
          {Array.from({ length: 4 }).map((_, j) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
            <TableCell key={`cell-${j}`}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyState() {
  const { t } = useTranslation('zones');
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <Layers className="h-10 w-10 text-jq-stone" strokeWidth={1.25} />
      <p className="text-[14px] font-medium text-jq-charcoal">{t('table.empty.title')}</p>
      <p className="max-w-sm text-[13px] text-jq-graphite">{t('table.empty.description')}</p>
    </div>
  );
}
